<template>
  <v-dialog v-model="dialog" max-width="500">
    <v-card>
      <v-card-title class="d-flex align-center">
        Describe Feedback
        <v-spacer />
        <v-btn icon variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p class="text-medium-emphasis mb-4">
          Share your thoughts, suggestions, or report issues. We appreciate your input!
        </p>
        <v-textarea
          v-model="feedback"
          label="Your Feedback"
          placeholder="Describe your feedback here..."
          rows="4"
          variant="outlined"
        />
        <v-checkbox
          v-model="includeScreenshot"
          label="Include screenshot of current page"
          density="compact"
        />
      </v-card-text>
      <v-card-actions class="flex-column align-stretch">
        <p v-if="feedbackExceedsLimit" class="text-warning text-caption mb-2 text-center">
          Feedback is too long (max {{ MAX_FEEDBACK_CHARS.toLocaleString() }} characters).
        </p>
        <div class="d-flex w-100">
          <v-btn variant="text" @click="close">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" :disabled="feedbackExceedsLimit" :loading="submitting" @click="submit">Submit</v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-snackbar v-model="snackbar" :timeout="3000" color="success" location="bottom">
    Feedback sent. Thank you!
  </v-snackbar>
</template>

<script>
import axios from '@/axios.js';
import html2canvas from 'html2canvas';

const BASE_URL = process.env.VUE_APP_API_BASE_URL;
const SCREENSHOT_MAX_WIDTH = 1200;
const MAX_FEEDBACK_CHARS = 10000;

function truncateToChars(text, maxChars) {
  const t = text.trim();
  return t.length > maxChars ? t.slice(0, maxChars) : t;
}

export default {
  name: 'FeedbackModal',
  props: {
    modelValue: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      feedback: '',
      includeScreenshot: false,
      submitting: false,
      snackbar: false,
      MAX_FEEDBACK_CHARS
    };
  },
  computed: {
    dialog: {
      get() { return this.modelValue; },
      set(v) { this.$emit('update:modelValue', v); }
    },
    feedbackExceedsLimit() {
      return this.feedback.length > MAX_FEEDBACK_CHARS;
    }
  },
  methods: {
    close() {
      this.dialog = false;
    },
    async captureScreenshot() {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: window.devicePixelRatio || 1
      });
      let out = canvas;
      if (canvas.width > SCREENSHOT_MAX_WIDTH) {
        const ratio = SCREENSHOT_MAX_WIDTH / canvas.width;
        const h = Math.round(canvas.height * ratio);
        const resized = document.createElement('canvas');
        resized.width = SCREENSHOT_MAX_WIDTH;
        resized.height = h;
        resized.getContext('2d').drawImage(canvas, 0, 0, SCREENSHOT_MAX_WIDTH, h);
        out = resized;
      }
      return out.toDataURL('image/png');
    },
    async submit() {
      if (!this.feedback.trim()) return;
      this.submitting = true;
      const feedbackText = truncateToChars(this.feedback, MAX_FEEDBACK_CHARS);
      const includeScreenshot = this.includeScreenshot;
      this.feedback = '';
      this.includeScreenshot = false;
      this.close();

      await this.$nextTick();
      await new Promise((r) => setTimeout(r, 400));

      let screenshotData = null;
      if (includeScreenshot) {
        try {
          screenshotData = await this.captureScreenshot();
        } catch (err) {
          console.warn('Screenshot capture failed:', err);
        }
      }

      try {
        const config = {
          headers: {
            Authorization: localStorage.getItem('auth_token')
          }
        };
        const payload = {
          feedback: feedbackText,
          include_screenshot: includeScreenshot,
          screenshot_data: screenshotData
        };
        await axios.post(`${BASE_URL}/feedback`, payload, config);
        this.snackbar = true;
      } catch (e) {
        console.error('Feedback failed:', e);
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
